/**
 * Image Node for Lexical Editor
 */

import {
    DecoratorNode,
    $applyNodeReplacement,
} from 'lexical';
import React, { Suspense } from 'react';

function ImageComponent({ src, altText, width, height }) {
    return (
        <img
            src={src}
            alt={altText}
            style={{
                maxWidth: '100%',
                height: 'auto',
                width: width || 'auto',
            }}
            draggable="false"
        />
    );
}

export class ImageNode extends DecoratorNode {
    __src;
    __altText;
    __width;
    __height;

    static getType() {
        return 'image';
    }

    static clone(node) {
        return new ImageNode(
            node.__src,
            node.__altText,
            node.__width,
            node.__height,
            node.__key
        );
    }

    static importJSON(serializedNode) {
        const { src, altText, width, height } = serializedNode;
        const node = $createImageNode({ src, altText, width, height });
        return node;
    }

    static importDOM() {
        return {
            img: () => ({
                conversion: (domNode) => {
                    const img = domNode;
                    const { src, alt, width, height } = img;
                    const node = $createImageNode({
                        src,
                        altText: alt || '',
                        width: width || undefined,
                        height: height || undefined,
                    });
                    return { node };
                },
                priority: 0,
            }),
        };
    }

    constructor(src, altText, width, height, key) {
        super(key);
        this.__src = src;
        this.__altText = altText;
        this.__width = width;
        this.__height = height;
    }

    exportJSON() {
        return {
            type: 'image',
            version: 1,
            src: this.__src,
            altText: this.__altText,
            width: this.__width,
            height: this.__height,
        };
    }

    exportDOM() {
        const element = document.createElement('img');
        element.setAttribute('src', this.__src);
        element.setAttribute('alt', this.__altText);
        if (this.__width) {
            element.setAttribute('width', String(this.__width));
        }
        if (this.__height) {
            element.setAttribute('height', String(this.__height));
        }
        return { element };
    }

    createDOM() {
        const span = document.createElement('span');
        span.className = 'editor-image-wrapper';
        return span;
    }

    updateDOM() {
        return false;
    }

    getSrc() {
        return this.__src;
    }

    getAltText() {
        return this.__altText;
    }

    decorate() {
        return (
            <Suspense fallback={null}>
                <ImageComponent
                    src={this.__src}
                    altText={this.__altText}
                    width={this.__width}
                    height={this.__height}
                />
            </Suspense>
        );
    }
}

export function $createImageNode({ src, altText, width, height }) {
    return $applyNodeReplacement(new ImageNode(src, altText, width, height));
}

export function $isImageNode(node) {
    return node instanceof ImageNode;
}
